import React, { useCallback, useEffect, useMemo, useState } from 'react'
import message from 'antd/lib/message'
import classnames from 'classnames'
import moment from 'moment-timezone'

import { useAuthState, useUpdateUser } from 'hooks'
import { analytics, form, openNotificationWithMsg } from 'helpers'
import { USER } from 'helpers/constants'
import { getAddressText } from 'helpers/location'
import { valueOrEmptyString } from 'helpers/strings'
import { GENDER_OPTIONS, PREFERENCE_OPTIONS } from 'constants/user'
import { AUTH_STEPS, SIGNUP_STEPS } from 'constants/auth'

import AgeRangeSlider from 'components/AgeRangeSlider'
import AuthModal from 'components/AuthModal'
import BirthdateInput from 'components/BirthdateInput'
import PrimaryButton from 'components/PrimaryButton'
import UsernameInput from 'components/UsernameInput'

import ProfileSection from 'components/Profile/ProfileSection'
import ProfileSectionTitle from 'components/Profile/ProfileSectionTitle'
import ProfileGrid from 'components/Profile/ProfileGrid'
import ProfileFormGroup from 'components/Profile/ProfileFormGroup'
import ProfileLabel from 'components/Profile/ProfileLabel'
import ProfileSelect from 'components/Profile/ProfileSelect'
import ProfileInput from 'components/Profile/ProfileInput'
import ProfileWrapper from 'components/Profile/ProfileWrapper'
import ProfileStickyButton from 'components/Profile/ProfileStickyButton'

import {
  FORM_CONTROL_BG,
  FORM_CONTROL_BG_COLOR,
  FORM_CONTROL_CLASS,
  FORM_CONTROL_WIDTH,
  FORM_RADIUS,
} from 'components/Profile/constants'
import ProfileMultiSelect from 'components/Profile/ProfileMultiSelect'
import ProfileSectionStickyTitle from 'components/Profile/ProfileSectionStickyTitle'
import ProfileLocation from 'components/Profile/ProfileLocation'

const { emailIsValid } = form

const Settings = () => {
  const { user } = useAuthState()
  const [editedUser, setEditedUser] = useState(user)
  const [updatedValues, setUpdatedValues] = useState({})
  const [showAuthModal, setShowAuthModal] = useState(false)

  const { update } = useUpdateUser()
  const hasChanges = useMemo(() => !!Object.keys(updatedValues).length, [updatedValues])

  useEffect(() => {
    if (user) {
      setEditedUser({
        ...user,
        profileImage: user.profileImage || USER.PROFILE_IMG.DEFAULT,
      })
    }
  }, [user])

  const handleChange = updates => {
    const updateValues = existingValues => {
      const _updates = { ...existingValues }

      Object.keys(updates).forEach(key => {
        const value = updates[key]

        if (typeof value === 'object' && ['preference', 'userAddress'].includes(key)) {
          _updates[key] = {
            ...(existingValues[key] || {}),
            ...value,
          }
        } else {
          _updates[key] = value
        }
      })

      return _updates
    }

    setUpdatedValues(updateValues)
    setEditedUser(updateValues)
  }

  const handleInputChange = ({ target: { name, value } }) => handleChange({ [name]: value })

  const handleSelectChange = ({ target: { name, value } }) =>
    handleChange({ [name]: value || null })

  const handleSave = useCallback(
    async e => {
      e.preventDefault()
      if (!form.checkValidity(e)) {
        return
      }

      if (
        typeof updatedValues.location !== 'undefined' &&
        (!updatedValues.geoLat || !updatedValues.geoLat)
      ) {
        openNotificationWithMsg('error', 'Please fill out your location!')
        return
      }

      if (editedUser.email && !emailIsValid(editedUser.email)) {
        message.error('Please use a valid email address')
        return
      }

      if (Object.keys(updatedValues).length) {
        message.loading('saving...', 0)

        try {
          const response = await update(updatedValues)
          if (response) {
            message.destroy()
            setUpdatedValues({})
            message.success('Settings Saved', 2.5)
            analytics.segmentTrack('User Updated Settings', updatedValues)
          }
        } catch (error) {
          message.destroy()
          message.error(error.message || 'Please try again')
        }
      } else {
        message.info('No changes provided for update!')
      }
    },
    [update, updatedValues, editedUser]
  )

  return (
    <>
      <form noValidate onSubmit={handleSave}>
        <ProfileWrapper user={editedUser?.id ? editedUser : user}>
          <div className="pb-3">
            <ProfileSectionStickyTitle hideBorder={true} hasChanges={hasChanges}>
              Account
            </ProfileSectionStickyTitle>
            <div
              className={classnames('max-w-sm md:pb-0 md:h-28', {
                'pb-4': hasChanges,
                'pb-12': !hasChanges,
              })}
            >
              <div className="text-lightGray text-sm">
                Your account information is not public to other daters. Please do not share your
                settings or account information with anyone.
              </div>
            </div>
            {hasChanges && <ProfileStickyButton type="submit">Save Profile</ProfileStickyButton>}
            <ProfileSection>
              <ProfileSectionTitle>Account Information</ProfileSectionTitle>
              <ProfileGrid>
                <ProfileFormGroup>
                  <ProfileLabel>First Name</ProfileLabel>
                  <ProfileInput
                    showRequired
                    placeholder="First Name"
                    value={valueOrEmptyString(editedUser.firstName)}
                    type="text"
                    name="firstName"
                    disabled
                  />
                </ProfileFormGroup>
                <ProfileFormGroup>
                  <ProfileLabel>Last Name</ProfileLabel>
                  <ProfileInput
                    showRequired
                    placeholder="Last Name"
                    value={valueOrEmptyString(editedUser.lastName)}
                    type="text"
                    name="firstName"
                    disabled
                  />
                </ProfileFormGroup>
                <ProfileFormGroup>
                  <ProfileLabel>Username</ProfileLabel>
                  <UsernameInput
                    name="username"
                    required
                    Input={ProfileInput}
                    defaultValue={editedUser.username}
                    onChange={handleInputChange}
                    disabled
                  />
                </ProfileFormGroup>
                <ProfileFormGroup>
                  <ProfileLabel>Email Address</ProfileLabel>
                  <ProfileInput
                    type="email"
                    value={valueOrEmptyString(editedUser.email)}
                    name="email"
                    onChange={handleInputChange}
                    errorMessage={
                      !editedUser.email || emailIsValid(editedUser.email)
                        ? false
                        : 'Invalid email address'
                    }
                    disabled
                  />
                </ProfileFormGroup>
                <ProfileFormGroup>
                  <ProfileLabel>Mailing Address</ProfileLabel>
                  <ProfileLocation
                    showRequired
                    searchType="address"
                    initialLocation={getAddressText(editedUser.userAddress)}
                    onLocationUpdate={({ components }) => {
                      handleChange({
                        userAddress: components || {},
                      })
                    }}
                  />
                </ProfileFormGroup>
                <ProfileFormGroup className="md:self-end">
                  <ProfileInput
                    placeholder="Apartment / Suite etc."
                    value={valueOrEmptyString(editedUser.userAddress?.appt)}
                    type="text"
                    name="appt"
                    onChange={e =>
                      handleChange({
                        userAddress: {
                          appt: e.target.value,
                        },
                      })
                    }
                  />
                </ProfileFormGroup>
              </ProfileGrid>
            </ProfileSection>
            <ProfileSection>
              <ProfileSectionTitle>About me</ProfileSectionTitle>
              <ProfileGrid>
                <ProfileFormGroup>
                  <ProfileLabel>My Gender</ProfileLabel>
                  <ProfileSelect
                    name="gender"
                    options={GENDER_OPTIONS}
                    onChange={handleSelectChange}
                    value={valueOrEmptyString(editedUser.gender)}
                    showDefault
                    showRequired
                  />
                </ProfileFormGroup>
                <ProfileFormGroup>
                  <ProfileLabel>My Birthday</ProfileLabel>
                  <BirthdateInput
                    Input={ProfileInput}
                    value={valueOrEmptyString(editedUser.birthdate)}
                    name="birthdate"
                    onChange={handleInputChange}
                    showRequired
                  />
                </ProfileFormGroup>
                <ProfileFormGroup>
                  <ProfileLabel>Homebase</ProfileLabel>
                  <ProfileLocation
                    showRequired
                    initialLocation={editedUser.location}
                    onLocationUpdate={({ geoLat, geoLng, location }) => {
                      setUpdatedValues({
                        ...updatedValues,
                        geoLat,
                        geoLng,
                        location,
                        timestamp: moment().unix(),
                      })
                      setEditedUser({
                        ...editedUser,
                        geoLat,
                        geoLng,
                        location,
                        timestamp: moment().unix(),
                      })
                    }}
                  />
                </ProfileFormGroup>
              </ProfileGrid>
            </ProfileSection>
            <ProfileSection>
              <ProfileSectionTitle>Dating Preferences</ProfileSectionTitle>
              <ProfileGrid>
                <ProfileFormGroup>
                  <ProfileLabel>Gender Interest</ProfileLabel>
                  <ProfileMultiSelect
                    showRequired
                    className={classnames(FORM_CONTROL_WIDTH, FORM_RADIUS)}
                    name="preference.genders"
                    options={PREFERENCE_OPTIONS}
                    value={editedUser.preference.genders}
                    onChange={({ values }) => {
                      handleChange({
                        preference: {
                          genders: values.map(({ value }) => value),
                        },
                      })
                    }}
                  />
                </ProfileFormGroup>
                <ProfileFormGroup>
                  <ProfileLabel>AGE RANGE</ProfileLabel>
                  <AgeRangeSlider
                    className={classnames(FORM_CONTROL_BG, 'text-white')}
                    min={editedUser.preference?.minAge}
                    max={editedUser.preference?.maxAge}
                    onChange={({ min, max }) => {
                      handleChange({ preference: { minAge: min, maxAge: max } })
                    }}
                  />
                </ProfileFormGroup>
                {user.ignore && (
                  <ProfileFormGroup>
                    <ProfileLabel>
                      Account Type{' '}
                      <span className="text-monaLisaPink">
                        ({user.profileVisible ? 'Date & Meet' : 'Hidden'})
                      </span>
                    </ProfileLabel>
                    <PrimaryButton
                      type="button"
                      className={classnames(FORM_CONTROL_CLASS, 'text-opacity-50')}
                      onClick={() => setShowAuthModal(true)}
                      style={{ background: FORM_CONTROL_BG_COLOR }}
                    >
                      Change
                    </PrimaryButton>
                  </ProfileFormGroup>
                )}
              </ProfileGrid>
            </ProfileSection>
          </div>
        </ProfileWrapper>
      </form>
      <AuthModal
        show={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onComplete={() => setShowAuthModal(false)}
        authStep={AUTH_STEPS.SIGN_UP}
        signupStep={SIGNUP_STEPS.PROFILE_VISIBILITY}
      />
    </>
  )
}

export default Settings
